// Libraries
import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { ActionService } from 'src/app/services/api/actions/action.service';
import { UserService } from 'src/app/services/api/users/user.service';

// Interfaces
import {
  Action,
  ActionPriority,
  ActionStatus,
  ActionType,
  defaultAction,
  PostAction,
  PutAction,
} from 'src/app/interfaces/api/action';
import { User } from 'src/app/interfaces/api/user';

// Classes
import { Pop } from 'src/app/classes/shared/pop';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

// Components
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { ButtonComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';

@Component({
  selector: 'app-link-action-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatListModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTabsModule,
    TranslateModule,
    TimeFormatPipe,
    ButtonComponent,
    SearchBarComponent,
    SpinnerComponent,
    NoDataComponent,
  ],
  templateUrl: './link-action-dialog.component.html',
  styleUrls: ['./link-action-dialog.component.scss'],
})
export class LinkActionDialogComponent implements OnInit, OnDestroy {
  public readonly data: { riskId: string; riskTitle?: string; projectId?: string } = inject(MAT_DIALOG_DATA);
  private readonly dialogRef: MatDialogRef<LinkActionDialogComponent> = inject(MatDialogRef);
  private readonly actionService: ActionService = inject(ActionService);
  private readonly userService: UserService = inject(UserService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly pop: Pop = inject(Pop);

  selectedTab: number = 0;

  // Create-tab state
  newAction: Partial<Action> = { ...defaultAction };
  users: User[] = [];
  typeOptions: ActionType[] = ['corrective', 'preventive', 'improvement'];
  priorityOptions: ActionPriority[] = ['low', 'medium', 'high', 'critical'];
  statusOptions: ActionStatus[] = ['todo', 'in_progress', 'done', 'closed', 'cancelled'];

  // Link-tab state
  loadingList: boolean = false;
  searchText: string = '';
  selectedId: string | null = null;
  actions: Action[] = [];
  filteredActions: Action[] = [];

  saving: boolean = false;
  destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.newAction = {
      ...defaultAction,
      source: 'risk',
      risks: [this.data.riskId],
      riskTitles: this.data.riskTitle ? [this.data.riskTitle] : [],
      title: this.translate.instant('actions.from_risk_prefix') + ' ' + (this.data.riskTitle || ''),
    };

    this.loadActions();
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUsers(): void {
    if (!this.data.projectId) return;
    this.userService.getUsersLight(this.data.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: User[]) => this.users = res || [],
      error: () => this.users = [],
    });
  }

  loadActions(): void {
    this.loadingList = true;
    const query: any = {};
    if (this.data.projectId) query.project = this.data.projectId;

    this.actionService.getActions(query).pipe(takeUntil(this.destroy$)).subscribe({
      next: (actions) => {
        this.actions = (actions || []).filter(a => !this.actionRiskIds(a).includes(this.data.riskId));
        this.applySearch();
        this.loadingList = false;
      },
      error: (err) => {
        console.error(err);
        this.actions = [];
        this.filteredActions = [];
        this.loadingList = false;
      },
    });
  }

  search(text: string = ''): void {
    this.searchText = text;
    this.applySearch();
  }

  applySearch(): void {
    const q = this.searchText.trim().toLowerCase();
    if (!q) {
      this.filteredActions = this.actions;
      return;
    }
    this.filteredActions = this.actions.filter(a =>
      (a.title || '').toLowerCase().includes(q) ||
      (a.description || '').toLowerCase().includes(q)
    );
  }

  create(): void {
    if (!this.newAction.title || !this.newAction.title.trim()) {
      this.pop.error(this.translate.instant('actions.title_required'));
      return;
    }

    this.saving = true;
    const post: PostAction = {
      project: this.data.projectId,
      title: this.newAction.title!,
      description: this.newAction.description,
      type: this.newAction.type as ActionType,
      priority: this.newAction.priority as ActionPriority,
      status: this.newAction.status as ActionStatus,
      source: 'risk',
      assignee: this.newAction.assignee as string,
      dueDate: this.newAction.dueDate,
      risks: [this.data.riskId],
      riskTitles: this.data.riskTitle ? [this.data.riskTitle] : [],
    };

    this.actionService.addAction(post).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        this.pop.toast(this.translate.instant('actions.create_success'), 'success', 4000, 'save');
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        console.error(err);
        this.pop.error(this.translate.instant('actions.save_error'));
      },
    });
  }

  link(): void {
    if (!this.selectedId) {
      this.pop.error(this.translate.instant('actions.select_required'));
      return;
    }

    const target = this.actions.find(a => a.id === this.selectedId);
    const existingIds = target ? this.actionRiskIds(target) : [];
    const existingTitles = target?.riskTitles?.length ? [...target.riskTitles] : [];

    const nextIds = existingIds.includes(this.data.riskId)
      ? existingIds
      : [...existingIds, this.data.riskId];
    const nextTitles = this.data.riskTitle && !existingTitles.includes(this.data.riskTitle)
      ? [...existingTitles, this.data.riskTitle]
      : existingTitles;

    this.saving = true;
    const put: PutAction = {
      id: this.selectedId,
      risks: nextIds,
      riskTitles: nextTitles,
    };

    this.actionService.updateAction(put).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        this.pop.toast(this.translate.instant('actions.link_success'), 'success', 4000, 'link');
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        console.error(err);
        this.pop.error(this.translate.instant('actions.link_error'));
      },
    });
  }

  submit(): void {
    if (this.selectedTab === 0) this.create();
    else this.link();
  }

  private actionRiskIds(a: Action): string[] {
    const list = a.risks && a.risks.length
      ? a.risks
      : ((a as any).risk ? [(a as any).risk] : []);
    return list.map((r: any) =>
      r && typeof r === 'object' && 'id' in r ? r.id : (r as string)
    );
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
